import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, MapPin, Search, X } from 'lucide-react';
import { SERVED_REGIONS, SERVED_REGION_COUNTRIES, servedRegionName } from '@evi/shared';
import { regionButtonLabel, regionGroups, regionPanelPlacement, toggleRegion, toggleRegionGroup } from '@/lib/regionPicker';
import styles from './RegionPicker.module.css';

/**
 * A multi-select of US states and Canadian provinces, grouped by country
 * (Warren, 25 October 2026). One control for the partner list's region
 * filter, the partner profile's "Regions served" and an SP Manager's
 * territory. Controlled: every tick calls onChange with the new list in
 * list order. The panel is portalled to the page body and placed from the
 * button (regionPanelPlacement), so a table or card never covers it and it
 * never runs off the screen; on a phone it is a bottom sheet.
 */
interface Props {
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** What the control picks, for its button and dialog ("Regions served"). */
  label: string;
  /** The button text with nothing picked ("Any region", "Choose regions"). */
  emptyLabel: string;
  disabled?: boolean;
  className?: string;
}

const PHONE = '(max-width: 639px)';

export function RegionPicker({ value, onChange, label, emptyLabel, disabled, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const panelId = useId();
  const selected = useMemo(() => new Set(value), [value]);
  const groups = useMemo(() => regionGroups(query), [query]);
  const phone = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE).matches;

  const measure = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPlace(regionPanelPlacement(rect, { width: window.innerWidth, height: window.innerHeight }) as CSSProperties);
  }, []);

  useLayoutEffect(() => { if (open && !phone) measure(); }, [open, phone, measure]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); } };
    const onMove = () => { if (!phone) measure(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    searchRef.current?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, phone, measure]);

  const panel = open ? (
    <>
      {phone && <div className={styles.backdrop} aria-hidden onClick={() => setOpen(false)} />}
      <div ref={panelRef} className={`${styles.panel} ${phone ? styles.sheet : ''}`} id={panelId} role="dialog" aria-label={label}
        style={phone ? undefined : (place ?? { visibility: 'hidden' })}>
        <div className={styles.panelHead}>
          <span className={styles.panelTitle}>{label}</span>
          <span className={styles.count}>{value.length ? `${value.length} selected` : 'None selected'}</span>
          <button type="button" className={styles.close} aria-label="Close" onClick={() => setOpen(false)}><X size={14} /></button>
        </div>
        <label className={styles.search}>
          <Search size={13} aria-hidden className={styles.searchIcon} />
          <input ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search states and provinces" aria-label="Search states and provinces" />
        </label>
        <div className={styles.groups}>
          {groups.map((g) => {
            const codes = g.regions.map((r) => r.code);
            const all = codes.length > 0 && codes.every((c) => selected.has(c));
            return (
              <fieldset key={g.country} className={styles.group}>
                <legend className={styles.groupHead}>
                  <span>{g.label}</span>
                  <button type="button" className={styles.groupToggle} onClick={() => onChange(toggleRegionGroup(value, codes, !all))}>
                    {all ? 'Clear' : query ? 'Select these' : 'Select all'}
                  </button>
                </legend>
                <div className={styles.options}>
                  {g.regions.map((r) => (
                    <label key={r.code} className={`${styles.option} ${selected.has(r.code) ? styles.optionOn : ''}`}>
                      <input type="checkbox" checked={selected.has(r.code)} onChange={() => onChange(toggleRegion(value, r.code))} />
                      <span className={styles.box} aria-hidden>{selected.has(r.code) && <Check size={11} />}</span>
                      <span className={styles.optionName}>{r.name}</span>
                      <span className={styles.optionCode}>{r.code}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}
          {!groups.length && <p className={styles.empty}>No state or province matches &ldquo;{query}&rdquo;.</p>}
        </div>
        <div className={styles.panelFoot}>
          <button type="button" className={styles.linkBtn} disabled={!value.length} onClick={() => onChange([])}>Clear all</button>
          <button type="button" className={styles.doneBtn} onClick={() => setOpen(false)}>Done</button>
        </div>
      </div>
    </>
  ) : null;

  return (
    <div className={`${styles.wrap} ${className ?? ''}`} ref={wrapRef}>
      <button ref={triggerRef} type="button" className={`${styles.trigger} ${value.length ? styles.triggerActive : ''}`} disabled={disabled}
        aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? panelId : undefined} aria-label={`${label}: ${regionButtonLabel(value, emptyLabel)}`}
        onClick={() => setOpen((v) => !v)}>
        <MapPin size={13} aria-hidden />
        <span className={styles.triggerText}>{regionButtonLabel(value, emptyLabel)}</span>
        <ChevronDown size={13} aria-hidden className={open ? styles.chevronOpen : styles.chevron} />
      </button>
      {panel && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </div>
  );
}

/** The regions as chips, each with its full name on hover; the empty text when none. */
export function RegionChips({ codes, empty = 'None recorded' }: { codes: readonly string[] | null | undefined; empty?: string }) {
  const list = (codes ?? []).filter((c) => SERVED_REGIONS.some((r) => r.code === c));
  if (!list.length) return <span className={styles.none}>{empty}</span>;
  const byCountry = SERVED_REGION_COUNTRIES.map((c) => ({ ...c, codes: list.filter((code) => SERVED_REGIONS.find((r) => r.code === code)?.country === c.country) }))
    .filter((c) => c.codes.length);
  return (
    <span className={styles.chips}>
      {byCountry.map((c) => c.codes.map((code) => (
        <span key={code} className={styles.chip} title={`${servedRegionName(code)}, ${c.label}`}>{code}</span>
      )))}
    </span>
  );
}
